// =========================================================================
// Policy Status Metadata — Fuente de verdad del funnel de seguros
// =========================================================================
//
// Este archivo mapea cada valor posible de `policy_status` del modelo
// Insurance_policy del backend a:
//   - Una etiqueta amigable en español para la UI.
//   - El número de paso del funnel (orden cronológico de la compra).
//   - Una descripción de la acción del usuario hasta donde llegó.
//   - Opcionalmente, el componente del móvil (credit-apk) que disparó el cambio.
//
// El mapeo se construyó por inspección cruzada de:
//   - kredit-api-MT/models/insurance/insurance_policy.js (comentarios del schema)
//   - kredit-api-MT/services/insurance.js (asignaciones de policy_status)
//   - credit-apk/src/app/pages (vistas del flujo de compra)
//
// Si el backend incorpora un estado nuevo, agregarlo acá. Estados desconocidos
// se renderizan con fallback automático (ver `getPolicyStatusMetadata`).

export interface PolicyStatusMetadata {
  code: string;
  label: string;       // Etiqueta amigable en español
  funnelStep: number;  // Orden cronológico (1 = inicio, 9 = anulación)
  userAction: string;  // "Hasta:" que aparece en la UI
  mobileView?: string; // Componente móvil — no se muestra en UI, sirve para debug
  isTerminal?: boolean;
  isError?: boolean;
}

export const POLICY_STATUS_METADATA: Record<string, PolicyStatusMetadata> = {
  CREATED: {
    code: 'CREATED',
    label: 'Creada',
    funnelStep: 1,
    userAction: 'Seleccionó producto',
    mobileView: 'DataFormComponent'
  },
  PLAN_SELECTED: {
    code: 'PLAN_SELECTED',
    label: 'Plan seleccionado',
    funnelStep: 2,
    userAction: 'Eligió plan y frecuencia',
    mobileView: 'DataFormComponent'
  },
  DATA_CLIENT_CONFIRMED: {
    code: 'DATA_CLIENT_CONFIRMED',
    label: 'Datos confirmados',
    funnelStep: 3,
    userAction: 'Confirmó datos personales',
    mobileView: 'DataFormComponent'
  },
  LICENSE_UPLOADED: {
    code: 'LICENSE_UPLOADED',
    label: 'Carnet cargado',
    funnelStep: 4,
    userAction: 'Cargó el carnet',
    mobileView: 'AttachIdCardComponent'
  },
  VERIFIED: {
    code: 'VERIFIED',
    label: 'Vehículo verificado',
    funnelStep: 5,
    userAction: 'Verificó datos del vehículo',
    mobileView: 'VehicleDataComponent'
  },
  ISSUED: {
    code: 'ISSUED',
    label: 'Emitida',
    funnelStep: 6,
    userAction: 'Emisión confirmada por aseguradora'
    // No tiene mobileView — es un setter del backend tras integración con SM/SC
  },
  PAYMENT_RECORDED: {
    code: 'PAYMENT_RECORDED',
    label: 'Pago inicial registrado',
    funnelStep: 7,
    userAction: 'Registró el pago inicial',
    mobileView: 'RcvPaymentComponent'
  },
  COMPLETED: {
    code: 'COMPLETED',
    label: 'Pagado completo',
    funnelStep: 8,
    userAction: 'Pagó todas las cuotas',
    isTerminal: true
  },
  ANNULLED: {
    code: 'ANNULLED',
    label: 'Anulada',
    funnelStep: 9,
    userAction: 'Anulación manual o por sincronización',
    isTerminal: true
  },

  // Estados legacy / edge cases que la interface seguros.interface.ts contempla
  // pero que rara vez aparecen en el dashboard. Quedan documentados acá para
  // que el fallback no se dispare con ellos.
  ACTIVE: {
    code: 'ACTIVE',
    label: 'Activa',
    funnelStep: 8,
    userAction: 'Póliza activa',
    isTerminal: true
  },
  PENDING: {
    code: 'PENDING',
    label: 'Pendiente',
    funnelStep: 0,
    userAction: 'Pendiente de procesamiento'
  },
  ERROR: {
    code: 'ERROR',
    label: 'Error',
    funnelStep: 0,
    userAction: 'Error en el flujo',
    isError: true
  }
};

// Devuelve el metadata de un código. Si no está mapeado, devuelve un fallback
// neutral que evita romper la UI cuando el backend introduce un estado nuevo.
export function getPolicyStatusMetadata(code: string): PolicyStatusMetadata {
  return POLICY_STATUS_METADATA[code] || {
    code,
    label: code,
    funnelStep: 999,
    userAction: '—'
  };
}

// Ordena una lista de items que tengan campo `status` por el funnelStep
// correspondiente. Estados desconocidos quedan al final (funnelStep 999).
export function sortByFunnelStep<T extends { status: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const stepA = getPolicyStatusMetadata(a.status).funnelStep;
    const stepB = getPolicyStatusMetadata(b.status).funnelStep;
    return stepA - stepB;
  });
}

// =========================================================================
// Breakdown — labels para sub-segmentos del bucket
// =========================================================================
//
// El backend (services/insurance_console.js#getDashboardStatusDistributionService)
// devuelve el breakdown con `label` ya resuelto, pero si por alguna razón viene
// vacío o el cliente quiere overridearlo, estas funciones son la fuente única.

export type BreakdownDimension = 'by_plan_kind' | 'by_notification' | 'by_frequency';

const BREAKDOWN_DIMENSION_TITLES: Record<BreakdownDimension, string> = {
  by_plan_kind: 'Por tipo de pago',
  by_notification: 'Por notificación',
  by_frequency: 'Por frecuencia'
};

const BREAKDOWN_KEY_LABELS: Record<string, string> = {
  // by_plan_kind
  partial_credit: 'Pago inicial · cuotas pendientes',
  fully_paid_stuck: 'Pago completo (no promovido)',
  // by_notification
  notified_ok: 'Notificación OK',
  notification_error: 'Con error de notif.',
  // by_frequency
  ANUAL: 'Anual',
  SEMESTRAL: 'Semestral',
  TRIMESTRAL: 'Trimestral',
  MENSUAL: 'Mensual'
};

export function getBreakdownDimensionTitle(dim: BreakdownDimension): string {
  return BREAKDOWN_DIMENSION_TITLES[dim] || dim;
}

export function getBreakdownLabel(dim: BreakdownDimension, key: string, fallback?: string): string {
  return fallback || BREAKDOWN_KEY_LABELS[key] || key;
}

// Para que la UI muestre un ícono/color semántico junto al sub-bucket.
export function getBreakdownKeyTone(key: string): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (key) {
    case 'notified_ok':
    case 'partial_credit':
      return 'success';
    case 'notification_error':
      return 'warning';
    case 'fully_paid_stuck':
      return 'danger';
    default:
      return 'neutral';
  }
}
